/**
 * Storage Cleanup Utility
 * 
 * Handles cleanup of stale/corrupted localStorage data that can cause:
 * - 404 errors on room scores (stale room IDs)
 * - Bidding issues (stale game state)
 * - Authentication problems (expired tokens)
 */

export interface CleanupOptions {
  /** Clear all game-related data (rooms, game state) */
  clearGameData?: boolean;
  /** Clear authentication tokens */
  clearAuth?: boolean;
  /** Clear user preferences */
  clearPreferences?: boolean;
  /** Clear everything */
  clearAll?: boolean;
  /** Log cleanup actions to console */
  verbose?: boolean;
}

/**
 * Storage keys used by the application
 */
const STORAGE_KEYS = {
  // Authentication
  AUTH_TOKEN: 'authToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_DATA: 'userData',
  USER_ID: 'userId',
  TEMP_USER_ID: 'tempUserId',
  
  // Game state
  ROOM_ID: 'roomId',
  FRONTEND_ROOM_ID: 'frontendRoomId',
  GAME_STATE: 'gameState',
  CURRENT_ROOM: 'currentRoom',
  ACTIVE_ROOMS: 'activeRooms',
  
  // User preferences
  THEME: 'theme',
  SOUND_ENABLED: 'soundEnabled',
  NOTIFICATION_SETTINGS: 'notificationSettings',
  
  // Debug/development
  DEBUG_MODE: 'debugMode',
  VERBOSE_LOGGING: 'verboseLogging',
};

/**
 * Clean up localStorage based on provided options
 */
export function cleanupLocalStorage(options: CleanupOptions = {}): void {
  const { 
    clearGameData = false, 
    clearAuth = false, 
    clearPreferences = false, 
    clearAll = false,
    verbose = true 
  } = options;

  const log = verbose ? console.log : () => {};
  const itemsRemoved: string[] = [];

  if (clearAll) {
    log('🧹 [Storage Cleanup] Clearing ALL localStorage...');
    const allKeys = Object.keys(localStorage);
    localStorage.clear();
    itemsRemoved.push(...allKeys);
    log(`✅ [Storage Cleanup] Removed ${allKeys.length} items`);
    return;
  }

  // Clear game-related data
  if (clearGameData) {
    log('🎮 [Storage Cleanup] Clearing game data...');
    const gameKeys = [
      STORAGE_KEYS.ROOM_ID,
      STORAGE_KEYS.FRONTEND_ROOM_ID,
      STORAGE_KEYS.GAME_STATE,
      STORAGE_KEYS.CURRENT_ROOM,
      STORAGE_KEYS.ACTIVE_ROOMS,
    ];
    
    gameKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        itemsRemoved.push(key);
        log(`  ✓ Removed: ${key}`);
      }
    });

    // Remove any per-room cached settings (room_settings_<roomId>)
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('room_settings_')) {
          toRemove.push(key);
        }
      }
      toRemove.forEach(k => {
        localStorage.removeItem(k);
        itemsRemoved.push(k);
        log(`  ✓ Removed: ${k}`);
      });
    } catch {}
  }

  // Clear authentication data
  if (clearAuth) {
    log('🔐 [Storage Cleanup] Clearing auth data...');
    const authKeys = [
      STORAGE_KEYS.AUTH_TOKEN,
      STORAGE_KEYS.REFRESH_TOKEN,
      STORAGE_KEYS.USER_DATA,
      STORAGE_KEYS.USER_ID,
      STORAGE_KEYS.TEMP_USER_ID,
    ];
    
    authKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        itemsRemoved.push(key);
        log(`  ✓ Removed: ${key}`);
      }
    });
  }

  // Clear user preferences
  if (clearPreferences) {
    log('⚙️ [Storage Cleanup] Clearing user preferences...');
    const prefKeys = [
      STORAGE_KEYS.THEME,
      STORAGE_KEYS.SOUND_ENABLED,
      STORAGE_KEYS.NOTIFICATION_SETTINGS,
    ];
    
    prefKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        itemsRemoved.push(key);
        log(`  ✓ Removed: ${key}`);
      }
    });
  }

  if (itemsRemoved.length > 0) {
    log(`✅ [Storage Cleanup] Cleanup complete - ${itemsRemoved.length} items removed`);
  } else {
    log('ℹ️ [Storage Cleanup] No items to remove');
  }
}

/**
 * Check for stale room data and clean it up
 * Call this on app initialization or before joining a new game
 */
export function cleanupStaleRoomData(): void {
  console.log('🔍 [Storage Cleanup] Checking for stale room data...');
  
  const roomId = localStorage.getItem(STORAGE_KEYS.ROOM_ID);
  const frontendRoomId = localStorage.getItem(STORAGE_KEYS.FRONTEND_ROOM_ID);
  
  // If we have room IDs stored but no active game, they're stale
  if (roomId || frontendRoomId) {
    console.warn('⚠️ [Storage Cleanup] Found stale room data - cleaning up...');
    cleanupLocalStorage({ clearGameData: true, verbose: true });
  } else {
    console.log('✅ [Storage Cleanup] No stale room data found');
  }
}

/**
 * Export storage keys for use in other modules
 */
export { STORAGE_KEYS };

/**
 * Expose cleanup function to window for manual debugging
 */
if (typeof window !== 'undefined') {
  (window as any).cleanupStorage = cleanupLocalStorage;
  (window as any).cleanupStaleRooms = cleanupStaleRoomData;
  console.log('🛠️ [Storage Cleanup] Utilities available: window.cleanupStorage(), window.cleanupStaleRooms()');
}
